﻿import { useCallback, useEffect, useRef, useState } from "react";

type InsertSignal = {
  nonce: number;
  token: string;
};

type CommandSignal = {
  nonce: number;
  command:
    | "bold"
    | "italic"
    | "underline"
    | "strikeThrough"
    | "insertUnorderedList"
    | "insertOrderedList"
    | "justifyLeft"
    | "justifyCenter"
    | "justifyRight"
    | "removeFormat"
    | "undo"
    | "redo"
    | "fontName"
    | "fontSize"
    | "createLink"
    | "unlink"
    | "clear";
  value?: string;
};

type TemplateWysiwygEditorProps = {
  value: string;
  onChange: (next: string) => void;
  placeholders: string[];
  placeholder?: string;
  singleLine?: boolean;
  showPlaceholderPalette?: boolean;
  showToolbar?: boolean;
  onFocus?: () => void;
  isActive?: boolean;
  insertTokenSignal?: InsertSignal | null;
  commandSignal?: CommandSignal | null;
  showModeToggle?: boolean;
  viewMode?: "rich" | "html";
  onViewModeChange?: (mode: "rich" | "html") => void;
};

const TOKEN_REGEX = /(\{\{[^{}]+\}\})/g;
const TOKEN_PART_REGEX = /^\{\{[^{}]+\}\}$/;

const createTokenBadge = (token: string) => {
  const span = document.createElement("span");
  span.className = "template-token-badge";
  span.setAttribute("data-token", token);
  span.setAttribute(
    "data-token-id",
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
  span.setAttribute("contenteditable", "false");
  span.setAttribute("spellcheck", "false");
  span.setAttribute("draggable", "true");
  span.textContent = token;
  return span;
};

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");

const replaceTokenTextNodes = (node: Node) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent ?? "";
      if (!text.includes("{{")) return;
      const chunks = text.split(TOKEN_REGEX).filter((chunk) => chunk.length > 0);
      if (!chunks.some((part) => TOKEN_PART_REGEX.test(part))) return;
      const fragment = document.createDocumentFragment();
      chunks.forEach((part) => {
        if (TOKEN_PART_REGEX.test(part)) {
          fragment.appendChild(createTokenBadge(part));
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      });
      child.parentNode?.replaceChild(fragment, child);
      return;
    }

    if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as HTMLElement;
      if (element.dataset.token) return;
      replaceTokenTextNodes(element);
    }
  });
};

const renderTemplateValue = (root: HTMLElement, value: string, singleLine: boolean) => {
  const hasMarkup = /<[^>]+>/.test(value);
  const html = hasMarkup ? value : escapeHtml(value).replace(/\r?\n/g, "<br>");
  root.innerHTML = html;
  replaceTokenTextNodes(root);
  if (!root.innerHTML.trim() && !singleLine) root.innerHTML = "<br>";
};

const serializeTemplateValue = (root: HTMLElement, singleLine: boolean) => {
  const pickSerializableTypographyStyle = (element: HTMLElement) => {
    const style = element.style;
    const pairs: string[] = [];
    const push = (prop: string, value: string) => {
      const clean = String(value ?? "").trim();
      if (!clean) return;
      pairs.push(`${prop}:${clean}`);
    };
    push("font-weight", style.fontWeight);
    push("font-style", style.fontStyle);
    push("text-decoration", style.textDecoration);
    push("font-family", style.fontFamily);
    push("font-size", style.fontSize);
    push("line-height", style.lineHeight);
    push("color", style.color);
    return pairs.join(";");
  };

  const copy = root.cloneNode(true) as HTMLElement;
  copy.querySelectorAll<HTMLElement>("[data-token]").forEach((element) => {
    const next = element.nextSibling;
    if (next && next.nodeType === Node.TEXT_NODE && /^\s+$/.test(next.textContent ?? "")) {
      next.textContent = "";
    }
    const tokenValue = element.getAttribute("data-token") ?? element.textContent ?? "";
    const styleValue = pickSerializableTypographyStyle(element);
    if (styleValue) {
      const span = document.createElement("span");
      span.setAttribute("style", styleValue);
      span.textContent = tokenValue;
      element.replaceWith(span);
    } else {
      element.replaceWith(document.createTextNode(tokenValue));
    }
  });
  if (singleLine) {
    copy.innerHTML = copy.innerHTML.replace(/<br\s*\/?>/gi, " ").replace(/\r?\n/g, " ");
  }
  return copy.innerHTML.trim();
};

const isTokenAllowed = (token: string, placeholders: string[]) => placeholders.includes(token);

export const TemplateWysiwygEditor = ({
  value,
  onChange,
  placeholders,
  placeholder,
  singleLine = false,
  showPlaceholderPalette = true,
  showToolbar = true,
  onFocus,
  isActive = false,
  insertTokenSignal = null,
  commandSignal = null,
  showModeToggle = true,
  viewMode: controlledViewMode,
  onViewModeChange
}: TemplateWysiwygEditorProps) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const htmlRef = useRef<HTMLTextAreaElement | null>(null);
  const syncingRef = useRef(false);
  const handledInsertNonceRef = useRef<number | null>(null);
  const handledCommandNonceRef = useRef<number | null>(null);
  const draggingTokenIdRef = useRef<string | null>(null);
  const draggingBadgeRef = useRef<HTMLElement | null>(null);
  const droppedInsideEditorRef = useRef(false);
  const [internalViewMode, setInternalViewMode] = useState<"rich" | "html">("rich");
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = (mode: "rich" | "html") => {
    if (controlledViewMode !== undefined) {
      onViewModeChange?.(mode);
      return;
    }
    setInternalViewMode(mode);
  };
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (viewMode !== "rich") return;
    const editor = editorRef.current;
    if (!editor) return;
    const current = serializeTemplateValue(editor, singleLine);
    if (current === value) return;
    syncingRef.current = true;
    renderTemplateValue(editor, value, singleLine);
    syncingRef.current = false;
  }, [singleLine, value, viewMode]);

  const emitChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || syncingRef.current) return;
    onChange(serializeTemplateValue(editor, singleLine));
  }, [onChange, singleLine]);

  const insertTokenIntoHtml = useCallback(
    (token: string) => {
      const textarea = htmlRef.current;
      if (!textarea || !isTokenAllowed(token, placeholders)) return;
      const start = textarea.selectionStart ?? textarea.value.length;
      const end = textarea.selectionEnd ?? textarea.value.length;
      const current = value ?? "";
      const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
      onChange(next);
      requestAnimationFrame(() => {
        textarea.focus();
        const cursor = start + token.length;
        textarea.setSelectionRange(cursor, cursor);
      });
    },
    [onChange, placeholders, value]
  );

  const applyCommandToSelectedBadges = useCallback(
    (command: CommandSignal["command"], valueArg?: string) => {
      const editor = editorRef.current;
      if (!editor) return false;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return false;
      const range = selection.getRangeAt(0);
      const badges = Array.from(editor.querySelectorAll<HTMLElement>("[data-token]"));
      const selectedBadges = badges.filter((badge) => {
        try {
          return range.intersectsNode(badge);
        } catch {
          return false;
        }
      });
      if (!selectedBadges.length) return false;

      selectedBadges.forEach((badge) => {
        const computed = window.getComputedStyle(badge);
        if (command === "bold") {
          const isBold = Number.parseInt(computed.fontWeight || "400", 10) >= 600;
          badge.style.fontWeight = isBold ? "400" : "700";
          return;
        }
        if (command === "italic") {
          const isItalic = computed.fontStyle === "italic";
          badge.style.fontStyle = isItalic ? "normal" : "italic";
          return;
        }
        if (command === "underline") {
          const hasUnderline = (computed.textDecorationLine || "").includes("underline");
          badge.style.textDecoration = hasUnderline ? "none" : "underline";
          return;
        }
        if (command === "strikeThrough") {
          const hasStrike = (computed.textDecorationLine || "").includes("line-through");
          badge.style.textDecoration = hasStrike ? "none" : "line-through";
          return;
        }
        if (command === "fontName") {
          if (valueArg) badge.style.fontFamily = valueArg;
          return;
        }
        if (command === "fontSize") {
          const map: Record<string, string> = {
            "1": "10px",
            "2": "12px",
            "3": "14px",
            "4": "16px",
            "5": "18px",
            "6": "22px",
            "7": "28px"
          };
          if (valueArg && map[valueArg]) badge.style.fontSize = map[valueArg];
          return;
        }
        if (command === "removeFormat") {
          badge.style.fontWeight = "";
          badge.style.fontStyle = "";
          badge.style.textDecoration = "";
          badge.style.fontFamily = "";
          badge.style.fontSize = "";
        }
      });

      return true;
    },
    []
  );

  const insertToken = useCallback(
    (token: string, targetRange?: Range | null) => {
      const editor = editorRef.current;
      if (!editor || !isTokenAllowed(token, placeholders)) return;
      editor.focus();

      const selection = window.getSelection();
      const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const requestedRange = targetRange && editor.contains(targetRange.commonAncestorContainer) ? targetRange : null;
      const safeRange = requestedRange ?? (range && editor.contains(range.commonAncestorContainer) ? range : null);

      const badgeNode = createTokenBadge(token);
      const fragment = document.createDocumentFragment();
      fragment.appendChild(badgeNode);

      if (!safeRange) {
        editor.appendChild(fragment);
      } else {
        safeRange.deleteContents();
        safeRange.insertNode(fragment);
        safeRange.collapse(false);
      }

      if (selection) {
        const cursorRange = document.createRange();
        cursorRange.setStartAfter(badgeNode);
        cursorRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(cursorRange);
      }

      emitChange();
    },
    [emitChange, placeholders]
  );

  const createDropRange = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    const doc = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };

    if (doc.caretRangeFromPoint) {
      return doc.caretRangeFromPoint(event.clientX, event.clientY);
    }

    if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(event.clientX, event.clientY);
      if (!pos) return null;
      const range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
      return range;
    }

    return null;
  }, []);

  useEffect(() => {
    if (!insertTokenSignal || !isActive) return;
    if (handledInsertNonceRef.current === insertTokenSignal.nonce) return;
    handledInsertNonceRef.current = insertTokenSignal.nonce;
    if (viewMode === "html") {
      insertTokenIntoHtml(insertTokenSignal.token);
      return;
    }
    insertToken(insertTokenSignal.token);
  }, [insertToken, insertTokenIntoHtml, insertTokenSignal, isActive, viewMode]);

  const applyCommand = useCallback(
    (command: CommandSignal["command"], valueArg?: string) => {
      if (viewMode === "html") {
        if (command === "clear") onChange("");
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;

      if (command === "clear") {
        editor.focus();
        editor.innerHTML = "";
        emitChange();
        return;
      }

      if (command === "createLink") {
        editor.focus();
        const raw = String(valueArg ?? "").trim();
        if (!raw) return;
        const normalized = /^(https?:|mailto:|tel:)/i.test(raw) ? raw : `https://${raw}`;
        document.execCommand("createLink", false, normalized);
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const anchor =
            selection.anchorNode?.parentElement?.closest("a") ??
            selection.focusNode?.parentElement?.closest("a");
          if (anchor) {
            anchor.setAttribute("target", "_blank");
            anchor.setAttribute("rel", "noopener noreferrer");
          }
        }
        emitChange();
        return;
      }

      if (command === "bold" || command === "italic" || command === "underline" || command === "strikeThrough" || command === "removeFormat" || command === "fontName" || command === "fontSize") {
        const changedBadgeStyles = applyCommandToSelectedBadges(command, valueArg);
        if (changedBadgeStyles) {
          emitChange();
          return;
        }
      }

      editor.focus();
      document.execCommand(command, false, valueArg);
      const focused = editorRef.current;
      if (focused) replaceTokenTextNodes(focused);
      emitChange();
    },
    [applyCommandToSelectedBadges, emitChange, onChange, viewMode]
  );

  useEffect(() => {
    if (!commandSignal || !isActive) return;
    if (handledCommandNonceRef.current === commandSignal.nonce) return;
    handledCommandNonceRef.current = commandSignal.nonce;
    applyCommand(commandSignal.command, commandSignal.value);
  }, [applyCommand, commandSignal, isActive]);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);
      const editor = editorRef.current;
      if (!editor) return;
      const token = event.dataTransfer.getData("text/template-placeholder") || event.dataTransfer.getData("text/plain");
      if (!token || !isTokenAllowed(token, placeholders)) return;
      droppedInsideEditorRef.current = true;
      const sourceTokenId = event.dataTransfer.getData("text/template-token-id") || draggingTokenIdRef.current;
      const sourceBadge = sourceTokenId
        ? (editor.querySelector(`[data-token-id="${sourceTokenId}"]`) as HTMLElement | null)
        : null;
      const dropRange = createDropRange(event);

      if (sourceBadge) {
        const sourceRange = document.createRange();
        sourceRange.selectNode(sourceBadge);
        if (dropRange && sourceRange.compareBoundaryPoints(Range.START_TO_END, dropRange) <= 0 && sourceRange.compareBoundaryPoints(Range.END_TO_START, dropRange) >= 0) {
          draggingTokenIdRef.current = null;
          return;
        }
        sourceBadge.remove();
      }

      insertToken(token, dropRange);
      draggingTokenIdRef.current = null;
    },
    [createDropRange, insertToken, placeholders]
  );

  return (
    <div className="template-wysiwyg">
      {showModeToggle ? (
        <div className="template-wysiwyg-mode-toggle" role="group" aria-label="Editor-Ansicht">
          <button
            type="button"
            className={`template-toolbar-btn${viewMode === "rich" ? " is-active" : ""}`}
            onClick={() => setViewMode("rich")}
          >
            Editor
          </button>
          <button
            type="button"
            className={`template-toolbar-btn${viewMode === "html" ? " is-active" : ""}`}
            onClick={() => setViewMode("html")}
          >
            HTML
          </button>
        </div>
      ) : null}
      {showToolbar ? null : null}

      {viewMode === "html" ? (
        <textarea
          ref={htmlRef}
          className={`template-wysiwyg-html${singleLine ? " is-single-line" : ""}`}
          value={value}
          placeholder={placeholder ?? ""}
          onFocus={onFocus}
          onClick={onFocus}
          onChange={(event) => onChange(event.target.value)}
          onDrop={(event) => {
            event.preventDefault();
            const token = event.dataTransfer.getData("text/template-placeholder") || event.dataTransfer.getData("text/plain");
            if (!token || !isTokenAllowed(token, placeholders)) return;
            insertTokenIntoHtml(token);
          }}
        />
      ) : (
        <div
          ref={editorRef}
          className={`template-wysiwyg-editor${singleLine ? " is-single-line" : ""}${dragOver ? " is-drag-over" : ""}`}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder ?? ""}
          onFocus={onFocus}
          onClick={onFocus}
          onInput={() => {
            const editor = editorRef.current;
            if (editor) replaceTokenTextNodes(editor);
            emitChange();
          }}
          onBlur={emitChange}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, singleLine ? text.replace(/\r?\n/g, " ") : text);
          }}
          onKeyDown={(event) => {
            if (singleLine && event.key === "Enter") {
              event.preventDefault();
              return;
            }
            if (!singleLine && event.key === "Enter") {
              event.preventDefault();
              document.execCommand("insertLineBreak");
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
            event.dataTransfer.dropEffect = draggingTokenIdRef.current ? "move" : "copy";
          }}
          onDragStart={(event) => {
            droppedInsideEditorRef.current = false;
            draggingBadgeRef.current = null;
            const target = event.target as HTMLElement | null;
            let badge = target?.closest("[data-token]") as HTMLElement | null;
            if (!badge) {
              const selection = window.getSelection();
              const selectedElement =
                selection?.anchorNode instanceof Element
                  ? selection.anchorNode
                  : selection?.anchorNode?.parentElement ?? null;
              badge = selectedElement?.closest?.("[data-token]") as HTMLElement | null;
            }
            if (!badge) return;
            const token = badge.getAttribute("data-token") ?? badge.textContent ?? "";
            const tokenId = badge.getAttribute("data-token-id") ?? "";
            if (!token) return;
            event.dataTransfer.setData("text/template-placeholder", token);
            event.dataTransfer.setData("text/plain", token);
            if (tokenId) event.dataTransfer.setData("text/template-token-id", tokenId);
            event.dataTransfer.effectAllowed = "move";
            draggingTokenIdRef.current = tokenId || null;
            draggingBadgeRef.current = badge;
          }}
          onDragEnd={(event) => {
            const sourceBadge = draggingBadgeRef.current;
            const droppedInsideEditor = droppedInsideEditorRef.current;
            if (
              sourceBadge &&
              !droppedInsideEditor &&
              event.dataTransfer.dropEffect === "none" &&
              sourceBadge.isConnected
            ) {
              sourceBadge.remove();
              emitChange();
            }
            draggingTokenIdRef.current = null;
            draggingBadgeRef.current = null;
            droppedInsideEditorRef.current = false;
            setDragOver(false);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        />
      )}

      {showPlaceholderPalette ? (
        <div className="template-wysiwyg-placeholder-palette" aria-label="Platzhalter">
          {placeholders.map((token) => (
            <button
              key={token}
              type="button"
              className="template-wysiwyg-placeholder-chip"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData("text/template-placeholder", token);
                event.dataTransfer.setData("text/plain", token);
                event.dataTransfer.effectAllowed = "copy";
              }}
              onClick={() => insertToken(token)}
              title="Per Klick einfuegen oder per Drag-and-Drop in das Textfeld ziehen"
            >
              {token}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
